/**
 * The views over a contractor's to-do list: Today, Upcoming, All, Priority 1
 * and Completed.
 *
 * Gordon: "Look at apps like Todoist, google keep and apple reminders." Apple
 * Reminders calls these smart lists and Todoist calls them Today and Upcoming;
 * either way they are the same list read by a different question - what is
 * due, what is coming, what is urgent - and that is all they are here. There
 * is no second store and no second request: every view is worked out from
 * the one GET the page already makes, so ticking something off in Today is
 * ticking it off everywhere.
 *
 * All is the tree, with dragging; the others are flat, sorted by when, with a
 * faint "in: Parent" on a sub-task so it is not read out of context.
 */
import { addDays, daysBetween, isoDay, longDay, parseDay } from '../lib/quickAdd';

export const VIEWS = [
  { key: 'today', label: 'Today' },
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'all', label: 'All' },
  { key: 'p1', label: 'Priority 1' },
  { key: 'done', label: 'Completed' }
];
export const VIEW_KEYS = VIEWS.map((v) => v.key);

/*
 * Two more once lists can be shared (batch 4), only where there are lists:
 * ASSIGNED TO ME - the open to-dos somebody else gave me, from any list - and
 * DELEGATED - the ones I gave away into lists I am not in, which the server
 * sends apart from the rest (`delegated`) because they are not mine to tick.
 * The old single list has neither, so its pills stay the five above.
 */
export const SHARE_VIEWS = [
  { key: 'assigned', label: 'Assigned to me' },
  { key: 'delegated', label: 'Delegated' }
];
export const SHARE_VIEW_KEYS = SHARE_VIEWS.map((v) => v.key);

/** Given to me by somebody else - not one I put my own name on. */
export const assignedToMe = (t, me) => Boolean(me && t && t.assignee_key === me
  && t.assigned_by_key && t.assigned_by_key !== me);

const day = (t) => (t.due_date ? String(t.due_date).slice(0, 10) : null);
const prio = (t) => Number(t.priority) || 4;

/*
 * DEADLINES (073). A to-do may say "do it on Tuesday, must be done by
 * Friday". It is still listed ONCE, on the day it is planned for - except
 * that one with only a deadline is listed on its deadline (Upcoming), and
 * Today also pulls in anything whose deadline is today or already past,
 * whatever its due date says, under a heading of its own. So the 15th's VAT
 * return planned for the 20th cannot sit quietly in next week while the
 * deadline goes by.
 */
const deadlineOf = (t) => (t.deadline ? String(t.deadline).slice(0, 10) : null);
/** The day a to-do is listed on: its due date, or failing that its deadline. */
const planDay = (t) => day(t) || deadlineOf(t);
/** Due today or before, or its deadline today or before. */
const forToday = (t, today) => Boolean((day(t) && day(t) <= today) || (deadlineOf(t) && deadlineOf(t) <= today));

/**
 * How near a deadline is, which colours its chip: 'late' (gone), 'soon'
 * (today or within two days), 'later', or null with none.
 */
export function deadlineState(item, today) {
  const d = deadlineOf(item);
  if (!d) return null;
  if (d < today) return 'late';
  const t = new Date(`${today}T12:00:00Z`);
  t.setUTCDate(t.getUTCDate() + 2);
  return d <= t.toISOString().slice(0, 10) ? 'soon' : 'later';
}

/** Overdue, due today, or neither - which is what colours a date chip. */
export function dueState(item, today) {
  const d = day(item);
  if (!d) return null;
  if (d < today) return 'late';
  if (d === today) return 'today';
  return 'later';
}

/**
 * When-order: by day, then by time (an untimed item after the timed ones
 * that day, as Todoist puts "any time today" last), then most urgent first,
 * then the contractor's own order.
 */
export function byWhen(a, b) {
  const da = day(a) || '9999';
  const db = day(b) || '9999';
  if (da !== db) return da < db ? -1 : 1;
  const ta = a.due_time || '99';
  const tb = b.due_time || '99';
  if (ta !== tb) return ta < tb ? -1 : 1;
  return (prio(a) - prio(b)) || (Number(a.position ?? 0) - Number(b.position ?? 0))
    || String(a.created_at || '').localeCompare(String(b.created_at || ''));
}

/** Does an item answer a search? Title, notes and ticket number. */
export function matches(item, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  return [item.title, item.notes, item.ticket_number]
    .some((v) => v && String(v).toLowerCase().includes(q));
}

/** How many each view would show, for the badges on the switcher. */
export function viewCounts(open, done, today) {
  return {
    today: open.filter((t) => forToday(t, today)).length,
    upcoming: open.filter((t) => planDay(t) && planDay(t) >= today).length,
    all: open.length,
    p1: open.filter((t) => prio(t) === 1).length,
    done: done.length
  };
}

/**
 * A flat view as headed groups: [{ key, label, items }]. All is not here -
 * it is the tree, drawn by the page - and a search turns any view flat.
 */
export function viewGroups(view, open, done, today, query) {
  const hit = (t) => matches(t, query);
  if (view === 'done') {
    // A day each, newest first, Todoist's way: "Today · Tue 20 Oct",
    // "Yesterday · Mon 19 Oct", then "Sat 17 Oct". Within a day, the order
    // they came in (newest first).
    const by = new Map();
    for (const t of done.filter(hit)) {
      const at = t.done_at ? new Date(t.done_at) : null;
      const k = at && !Number.isNaN(at.getTime()) ? isoDay(at) : '';
      if (!by.has(k)) by.set(k, []);
      by.get(k).push(t);
    }
    return [...by.entries()].sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0)).map(([k, items]) => {
      if (!k) return { key: 'done-earlier', label: 'Earlier', items };
      const n = daysBetween(today, k);
      const label = n === 0 ? `Today · ${longDay(k)}` : n === -1 ? `Yesterday · ${longDay(k)}` : longDay(k);
      return { key: `done-${k}`, label, items };
    });
  }
  if (view === 'today') {
    const late = open.filter((t) => hit(t) && day(t) && day(t) < today).sort(byWhen);
    const now = open.filter((t) => hit(t) && day(t) === today).sort(byWhen);
    // Planned for later (or for no day), but the deadline is today or gone.
    const byDeadline = (a, b) => (deadlineOf(a) < deadlineOf(b) ? -1 : deadlineOf(a) > deadlineOf(b) ? 1 : byWhen(a, b));
    const due = open.filter((t) => hit(t) && !(day(t) && day(t) <= today)
      && deadlineOf(t) && deadlineOf(t) <= today).sort(byDeadline);
    return [
      { key: 'overdue', label: 'Overdue', items: late },
      { key: 'deadline', label: 'Deadline today or passed', items: due },
      { key: 'today', label: 'Today', items: now }
    ].filter((g) => g.items.length);
  }
  if (view === 'upcoming') {
    // A day each for this week - today and the six after it - then Later.
    const groups = [];
    const start = parseDay(today);
    for (let i = 0; i < 7; i += 1) {
      const d = isoDay(addDays(start, i));
      // A to-do with only a deadline is listed on its deadline (073).
      const items = open.filter((t) => hit(t) && planDay(t) === d).sort(byWhen);
      const n = daysBetween(today, d);
      const label = n === 0 ? `Today · ${longDay(d)}` : n === 1 ? `Tomorrow · ${longDay(d)}` : longDay(d);
      if (items.length) groups.push({ key: d, label, items });
    }
    const end = isoDay(addDays(start, 6));
    const later = open.filter((t) => hit(t) && planDay(t) && planDay(t) > end)
      .sort((a, b) => (planDay(a) < planDay(b) ? -1 : planDay(a) > planDay(b) ? 1 : byWhen(a, b)));
    if (later.length) groups.push({ key: 'later', label: 'Later', items: later });
    return groups;
  }
  if (view === 'p1') {
    return [{ key: 'p1', label: null, items: open.filter((t) => hit(t) && prio(t) === 1).sort(byWhen) }]
      .filter((g) => g.items.length);
  }
  // All, searched: every open match, in when-order, flat.
  return [{ key: 'found', label: null, items: open.filter(hit).sort(byWhen) }]
    .filter((g) => g.items.length);
}

/*
 * SMART LISTS (074), Apple Reminders' - each the same one list asked a
 * different question, worked out here like the views above, so ticking
 * something off in Flagged ticks it off everywhere:
 *
 *   FLAGGED    flagged (074's column on the to-do)
 *   OVERDUE    its date, or its deadline (073), already gone
 *   SCHEDULED  has a day - its date, or failing that its deadline - with the
 *              late ones first, then a day each for the week, then Later
 *   NO DATE    neither a date nor a deadline: the work nobody has planned
 *   WAITING    held up by another to-do that is still open (072)
 *
 * Which of them a person's sidebar shows, and in what order, is theirs to
 * choose (GET /todos/view-prefs); a server without 074 shows none of them.
 */
export const SMART_VIEWS = [
  { key: 'flagged', label: 'Flagged' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'nodate', label: 'No date' },
  { key: 'waiting', label: 'Waiting' }
];
export const SMART_VIEW_KEYS = SMART_VIEWS.map((v) => v.key);

const deadlineDay = (t) => (t.deadline ? String(t.deadline).slice(0, 10) : null);
const whenDay = (t) => day(t) || deadlineDay(t);
const late = (t, today) => Boolean((day(t) && day(t) < today) || (deadlineDay(t) && deadlineDay(t) < today));
const isWaiting = (t) => Array.isArray(t.waiting_on) && t.waiting_on.length > 0;

/** Which to-dos a smart view holds. */
const SMART_TEST = {
  flagged: (t) => Boolean(t.flagged),
  overdue: (t, today) => late(t, today),
  scheduled: (t) => Boolean(whenDay(t)),
  nodate: (t) => !whenDay(t),
  waiting: isWaiting
};

/** How many each smart view holds, for the sidebar's badges. */
export function smartCounts(open, today) {
  const out = {};
  for (const k of SMART_VIEW_KEYS) out[k] = open.filter((t) => SMART_TEST[k](t, today)).length;
  return out;
}

/** A smart view as headed groups, like viewGroups. */
export function smartGroups(view, open, today, query) {
  const test = SMART_TEST[view];
  if (!test) return [];
  const mine = open.filter((t) => test(t, today) && matches(t, query));
  if (view === 'scheduled') {
    const gone = mine.filter((t) => whenDay(t) < today).sort(byWhen);
    const ahead = viewGroups('upcoming', mine.filter((t) => whenDay(t) >= today), [], today, '');
    return [{ key: 'sched-late', label: 'Overdue', items: gone }, ...ahead].filter((g) => g.items.length);
  }
  if (view === 'nodate') {
    // Nothing to sort by when: the most urgent first, then the list's order.
    return [{ key: view, label: null, items: mine.sort((a, b) => (prio(a) - prio(b)) || byWhen(a, b)) }]
      .filter((g) => g.items.length);
  }
  return [{ key: view, label: null, items: mine.sort(byWhen) }].filter((g) => g.items.length);
}
